/**
 * フロント側 POST /api/hero クライアント + フォールバック。
 *
 * - Worker から 200 が返れば Gemini 合成画像を Blob 化して返す
 * - Worker から 4xx/5xx が返れば Wave 2 の processOne で元画像を PNG 化
 * - どちらも失敗したらエラーを投げる (呼び出し側がスキップ判断)
 *
 * DI: `fetchFn` と `fallbackProcess` を差し替え可能 (テスト用)。
 */

import { type ProcessOptions, type ProcessedImage, type Result, processOne } from '../image';
import { calculateCropRect } from '../image/crop';
import { CARSENSOR_SPEC } from '../image/types';
import type {
  ApiError,
  HeroComposeRequest,
  HeroComposeResponse,
  ImageMimeType,
} from '@shared/types';

export interface HeroResult {
  readonly blob: Blob;
  readonly width: number;
  readonly height: number;
  readonly mimeType: ImageMimeType;
  readonly fallbackUsed: boolean;
  readonly processingTimeMs: number;
}

export interface HeroClientConfig {
  readonly fetchFn?: typeof fetch;
  readonly fallbackProcess?: (
    file: File,
    options: ProcessOptions,
  ) => Promise<Result<ProcessedImage>>;
  readonly decodeBlob?: (blob: Blob) => Promise<ImageBitmap>;
  readonly endpoint?: string;
}

export async function composeHeroForFile(
  file: File,
  config: HeroClientConfig = {},
): Promise<HeroResult> {
  const fetchFn = config.fetchFn ?? fetch;
  const endpoint = config.endpoint ?? '/api/hero';
  const decode = config.decodeBlob ?? ((b) => createImageBitmap(b));
  const fallback = config.fallbackProcess ?? ((f, o) => processOne(f, o));

  try {
    const response = await callHeroApi(file, fetchFn, endpoint);
    return await decodeSuccess(response, decode);
  } catch (err) {
    const result = await fallback(file, { outputPng: true });
    if (!result.ok) {
      throw new Error(
        `Hero compose failed and fallback failed: ${result.error.message} (caused by: ${String(err)})`,
      );
    }
    const bitmap = await decode(result.value.blob);
    return {
      blob: result.value.blob,
      width: bitmap.width,
      height: bitmap.height,
      mimeType: result.value.mimeType,
      fallbackUsed: true,
      processingTimeMs: result.value.processingTimeMs,
    };
  }
}

async function callHeroApi(
  file: File,
  fetchFn: typeof fetch,
  endpoint: string,
): Promise<HeroComposeResponse> {
  const base64 = await fileToBase64(file);
  const body: HeroComposeRequest = {
    carImageBase64: base64,
    mimeType: isSupportedMime(file.type) ? file.type : 'image/jpeg',
  };
  const res = await fetchFn(endpoint, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const parsed = (await res.json().catch(() => ({}))) as Partial<ApiError>;
    throw new Error(`POST /api/hero returned ${res.status}: ${parsed.code ?? 'unknown'}`);
  }
  return (await res.json()) as HeroComposeResponse;
}

async function decodeSuccess(
  response: HeroComposeResponse,
  decode: (blob: Blob) => Promise<ImageBitmap>,
): Promise<HeroResult> {
  const rawBlob = base64ToBlob(response.imageBase64, response.mimeType);
  const bitmap = await decode(rawBlob);

  // Wave 18: Gemini が稀に 16:9 等 4:3 以外の比率で返すことがあるので、
  // ブラウザ側で強制的に 4:3 中央クロップして保証する。
  // これをしないと:
  //   1) カーセンサーの 4:3 厳守仕様に違反
  //   2) 動画スライドで 16:9 が混入すると xfade のフレームサイズ不整合が発生
  const forced = await ensure4x3(bitmap, rawBlob, response.mimeType);

  return {
    blob: forced.blob,
    width: forced.width,
    height: forced.height,
    mimeType: response.mimeType,
    fallbackUsed: response.fallbackUsed,
    processingTimeMs: response.processingTimeMs,
  };
}

/**
 * bitmap が 4:3 許容誤差内ならそのまま、違ったら中央クロップして再エンコード。
 * OffscreenCanvas が使えない環境 (テスト等) では元 Blob をそのまま返す保守挙動。
 */
async function ensure4x3(
  bitmap: ImageBitmap,
  originalBlob: Blob,
  mimeType: ImageMimeType,
): Promise<{ blob: Blob; width: number; height: number }> {
  const ratio = bitmap.width / bitmap.height;
  const delta = Math.abs(ratio - CARSENSOR_SPEC.ASPECT_RATIO);
  if (delta <= CARSENSOR_SPEC.ASPECT_TOLERANCE) {
    return { blob: originalBlob, width: bitmap.width, height: bitmap.height };
  }
  if (typeof OffscreenCanvas === 'undefined') {
    // jsdom 等 OffscreenCanvas 不在環境: crop せず元画像を返す (デグレード)
    return { blob: originalBlob, width: bitmap.width, height: bitmap.height };
  }
  const rect = calculateCropRect(bitmap.width, bitmap.height);
  const canvas = new OffscreenCanvas(rect.sw, rect.sh);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return { blob: originalBlob, width: bitmap.width, height: bitmap.height };
  }
  ctx.drawImage(bitmap, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, rect.sw, rect.sh);
  const croppedBlob = await canvas.convertToBlob({
    type: mimeType,
    ...(mimeType === 'image/jpeg' ? { quality: CARSENSOR_SPEC.JPEG_QUALITY } : {}),
  });
  return { blob: croppedBlob, width: rect.sw, height: rect.sh };
}

function isSupportedMime(mime: string): mime is ImageMimeType {
  return mime === 'image/jpeg' || mime === 'image/png';
}

/**
 * File → base64 (prefix なし)。10MB 程度の画像なら FileReader.readAsDataURL で OK。
 */
export async function fileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      const raw = reader.result;
      if (typeof raw !== 'string') {
        reject(new Error('FileReader returned non-string result'));
        return;
      }
      const comma = raw.indexOf(',');
      resolve(comma === -1 ? raw : raw.slice(comma + 1));
    };
    reader.onerror = (): void => reject(reader.error ?? new Error('FileReader error'));
    reader.readAsDataURL(file);
  });
}

/**
 * base64 (prefix なし) + mimeType → Blob。
 */
export function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}
